import React, { useEffect, useMemo, useState } from 'react';

import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import csvSvg from '@assets/img/icons/csv.svg';
import linkIcon from '@assets/img/icons/link.svg';
import linkSvg from '@assets/img/icons/link.svg';
import mailOpenSvg from '@assets/img/icons/mail-open.svg';
import mailSvg from '@assets/img/icons/mail.svg';
import sortSvg from '@assets/img/icons/sort.svg';
import { DeliveryRequestBatchSentDialog } from '@components/admin/booking/DeliveryRequestBatchSentDialog';
import { DeliveryRequestBatchSubmitDialog } from '@components/admin/booking/DeliveryRequestBatchSubmitDialog';
import { PaymentSlipFormDialog } from '@components/admin/booking/PaymentSlipFormDialog';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { ExportCSVDialog2 } from '@components/ExportCSVDialog2';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { DebugForm } from '@components/NextForms/DebugForm';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { resolveDeliveryDestinationOptions } from '@data/arrivalNotice';
import { customsRequestStrings } from '@data/customsRequest';
import { deliveryRequestStrings, PICKUP_DATE_ORDER } from '@data/deliveryRequest';
import { moneyStrings } from '@data/money';
import { useSearch } from '@hooks/next/useSearch';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMutation } from '@hooks/useMutation';
import { CustomsRequestStatus, CustomsRequestStatusName } from '@models/next/CustomsRequestStatus';
import {
  type DeliveryRequestListItem,
  type DeliveryRequestBatchSubmitAction,
  DeliveryRequestSearchQuery,
  DeliveryRequestDeliveryStatusName,
} from '@models/next/DeliveryRequest';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { DeliveryRequestStatusName } from '@models/next/DeliveryRequestStatus';
import { PaymentSlipHelper } from '@models/next/PaymentSlip';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { lineClamp, makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import {
  DEFAULT_PAGE_SIZE,
  formatDate,
  formatDateTime,
  mapOptional,
  mapQueriesData,
  toggleArray,
} from '@utils/types';
import { type Checklist, useChecklist } from '@utils/useChecklist';
import clsx from 'clsx';
import { stringify } from 'query-string';
import { Link } from 'react-router-dom';
import { pick, last, pipe } from 'remeda';

const strings = createStrings({
  ja: {
    title: `${deliveryRequestStrings.deliveryCase}一覧`,
    exportCSV: 'Export CSV',
    tagSearchInputPlaceholder:
      'D2D番号、配車番号、荷主CNEE、集荷先、納品先、車種、数量やトラック会社などの検索条件を入力してください',
    details: '詳細',
    complete: '完了',
    openDeliveryPriceDialog: '金額入力',
    salespersonJapan: (
      <>
        CS
        <br />
        (JP)
      </>
    ),
    notDelivered: '未納品',
    delivered: '納品済',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  deliveryRequestStatusContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
  },
  deliveryStatusFilter: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.middleText,
    ...common.textGray500,
    '& button': {
      padding: '0 9px',
    },
    '& button[data-selected="true"]': {
      ...common.textPrimary,
      ...common.fontWeight600,
    },
    '& > button:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
    },
    '& > button:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.grey[200]}`,
    },
  },
}));

const DeliveryStatusFilter = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(DeliveryRequestSearchQuery);

  return (
    <div className={classes.deliveryStatusFilter}>
      <button
        data-selected={search.delivery_status === 'お届け中'}
        onClick={() => updateSearch({ delivery_status: 'お届け中' })}
      >
        {strings.notDelivered}
      </button>
      <button
        data-selected={search.delivery_status === 'お届け完了'}
        onClick={() => updateSearch({ delivery_status: 'お届け完了' })}
      >
        {strings.delivered}
      </button>
      <button
        data-selected={search.delivery_status == null}
        onClick={() => updateSearch({ delivery_status: undefined })}
      >
        ALL
      </button>
    </div>
  );
};

interface DeliveryRequestDeliveryPriceDisplayEditProps {
  deliveryRequest: DeliveryRequestListItem;
}

const DeliveryRequestDeliveryPriceDisplayEdit: React.FC<
  DeliveryRequestDeliveryPriceDisplayEditProps
> = ({ deliveryRequest }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [open, setOpen] = useState(false);
  const dialogKey = useDialogKey(open);
  const { confirm, renderDialog } = useConfirmDialog();

  const { d2d_no } = deliveryRequest.booking_inquiry;

  const bookingInquiryQuery = useBookingInquiryDetailByD2dNo(d2d_no, {
    enabled: open,
  });

  const preAlertQuery = dashboardPreAlertApis.useGet(
    '/dashboard/pre-alert/:d2d_no',
    {
      params: {
        d2d_no,
      },
    },
    {
      enabled: open,
    },
  );

  const isLoading =
    (bookingInquiryQuery.isLoading && bookingInquiryQuery.isFetching) ||
    (preAlertQuery.isLoading && preAlertQuery.isFetching);

  const totalPrice = useMemo(
    () =>
      mapOptional(deliveryRequest.payment_slip, (p) => new PaymentSlipHelper(p).getTotalAmount()),
    [deliveryRequest.payment_slip],
  );

  const canMutate = DeliveryRequestHelper.canMutate(deliveryRequest);

  return (
    <>
      {renderDialog()}
      <div className={classes.preEllipsis}>
        {totalPrice != null ? (
          <>
            {moneyStrings.formatYenFront(totalPrice)}
            <button style={{ verticalAlign: 'middle', marginLeft: 4 }}>
              <Link
                to={{
                  pathname: url('/admin/booking/PaymentSlipDetails'),
                  search: stringify({
                    id: deliveryRequest.payment_slip?.id,
                    d2d_no: deliveryRequest.booking_inquiry.d2d_no,
                  }),
                }}
                target="_blank"
              >
                <img
                  src={linkSvg}
                  style={{ width: 18, height: 18, display: 'block', position: 'relative', top: -1 }}
                />
              </Link>
            </button>
          </>
        ) : (
          <Button
            size="small"
            shape="round"
            color={foldMessagesEither(
              canMutate,
              () => 'grey',
              () => 'primary',
            )}
            isLoading={isLoading}
            text={isLoading ? undefined : strings.openDeliveryPriceDialog}
            onClick={() =>
              foldMessagesEither(
                canMutate,
                ({ messages }) => {
                  confirm({ messages });
                },
                () => setOpen(true),
              )
            }
            style={{ width: 80 }}
          />
        )}

        {mapQueriesData(
          { bookingInquiry: bookingInquiryQuery, preAlert: preAlertQuery },
          ({ bookingInquiry, preAlert }) => {
            return (
              <PaymentSlipFormDialog
                open={open}
                onClose={() => setOpen(false)}
                deliveryRequest={deliveryRequest}
                bookingInquiry={bookingInquiry}
                preAlert={preAlert}
                key={dialogKey}
                type="create"
              />
            );
          },
        )}
      </div>
    </>
  );
};

interface DeliveryRequestDeliveryStatusDisplayEditProps {
  deliveryRequest: DeliveryRequestListItem;
}

const DeliveryRequestDeliveryStatusDisplayEdit: React.FC<
  DeliveryRequestDeliveryStatusDisplayEditProps
> = ({ deliveryRequest }) => {
  const canMutate = DeliveryRequestHelper.canMutate(deliveryRequest);

  const [deliveryRequestBatchSubmitDialogOpen, setDeliveryRequestBatchSubmitDialogOpen] =
    useState(false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderDeliveryStatus = (item: DeliveryRequestListItem) => {
    const lastStatus = item.delivery_status.at(-1)!;
    const lastStatusName = lastStatus.name;

    return (
      <div style={{ width: 'max-content' }}>
        <div>
          {item.delivery_sent_file ? (
            <a
              href={item.delivery_sent_file.url}
              download={item.delivery_sent_file.name}
              className={clsx(classes.link, classes.underline)}
            >
              {lastStatusName}
            </a>
          ) : (
            lastStatusName
          )}
        </div>
        <div className={classes.exSmallerText}>{formatDateTime(lastStatus.time)}</div>
      </div>
    );
  };

  const { renderDialog, confirm } = useConfirmDialog();

  return last(deliveryRequest.delivery_status)?.name === 'お届け完了' ? (
    renderDeliveryStatus(deliveryRequest)
  ) : (
    <div>
      {renderDialog()}

      <Button
        size="small"
        shape="round"
        color={foldMessagesEither(
          canMutate,
          () => 'grey',
          () => 'primary',
        )}
        text={deliveryRequestStrings.deliverySent}
        onClick={() => {
          foldMessagesEither(
            canMutate,
            ({ messages }) => {
              confirm({
                messages,
              });
            },
            () => {
              setDeliveryRequestBatchSubmitDialogOpen(true);
            },
          );
        }}
      />

      <DeliveryRequestBatchSentDialog
        open={deliveryRequestBatchSubmitDialogOpen}
        onClose={() => setDeliveryRequestBatchSubmitDialogOpen(false)}
        deliveryRequests={[deliveryRequest]}
        type="single"
      />
    </div>
  );
};

interface DeliveryRequestTableProps {
  initialIndex: number;
  items: DeliveryRequestListItem[];
  checklist: Checklist<number>;
}

const DeliveryRequestTable: React.FC<DeliveryRequestTableProps> = React.memo((props) => {
  const { initialIndex, items, checklist } = props;

  const { setChecked, toggleCheckAll, allCheckedAndNotEmpty, checked } = checklist;

  const { search, updateSearch } = useSearch(DeliveryRequestSearchQuery);

  const queryClient = useQueryClient();

  const user = useUser();

  const completeDeliveryRequest = useMutation((id: number) =>
    dashboardDeliveryRequestApis.client.patch(
      '/dashboard/delivery-request/:id',
      {
        status_name: '配車完了',
      },
      {
        params: { id },
      },
    ),
  );

  const { renderSpinnerModal } = useFormUtils({ mutations: [completeDeliveryRequest] });

  const { renderDialog, confirm } = useConfirmDialog();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderDeliveryRequestStatus = (item: DeliveryRequestListItem) => {
    const lastStatus = item.status[item.status.length - 1];

    const canComplete = DeliveryRequestHelper.canComplete(item);

    if (!lastStatus) return <>-</>;

    const lastStatusName = lastStatus.name;

    return (
      <div className={classes.deliveryRequestStatusContainer}>
        <div style={{ width: 'max-content' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '60px auto', columnGap: 5 }}>
            <div>{lastStatusName}</div>
            <div>
              {lastStatusName === 'メール送信済未読' && (
                <img
                  src={mailSvg}
                  style={{ width: 18, height: 15, position: 'relative', top: 3 }}
                />
              )}

              {lastStatusName === 'メール送信済既読' && (
                <img
                  src={mailOpenSvg}
                  style={{ width: 18, height: 18, position: 'relative', top: 4 }}
                />
              )}

              {lastStatusName === 'CSV送信済' && (
                <img src={csvSvg} style={{ width: 18, height: 15, position: 'relative', top: 3 }} />
              )}
            </div>
          </div>
          <div className={clsx(classes.smallerText, classes.textGray500)}>
            {formatDateTime(lastStatus.time)}
          </div>
        </div>

        {canComplete?._tag === 'Right' && (
          <Button
            size="small"
            shape="round"
            color="primary"
            text={strings.complete}
            onClick={async () => {
              if (
                'confirmed' ===
                (await confirm({
                  message: deliveryRequestStrings.warningCompleteDeliveryRequest(
                    item.delivery_request_no,
                  ),
                }))
              ) {
                await completeDeliveryRequest.mutateAsync(item.id);
                queryClient.invalidateQueries(
                  dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request'),
                );
              }
            }}
          />
        )}
      </div>
    );
  };

  const renderCustomsRequestStatus = (status: CustomsRequestStatus) => {
    return (
      <div style={{ width: 'max-content' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '60px auto', columnGap: 5 }}>
          <div>{status.name}</div>
          <div>
            {status.attached_file && (
              <a
                className={classes.baseButton}
                target="_blank"
                rel="noreferrer"
                href={status.attached_file.url}
              >
                <img src={linkIcon} alt="tick" />
              </a>
            )}
          </div>
        </div>
        <div className={clsx(classes.smallerText, classes.textGray500)}>
          {formatDateTime(status.time)}
        </div>
      </div>
    );
  };

  const renderSortButton = (orderName: string) => (
    <img
      src={sortSvg}
      alt="Sort"
      className={classes.baseButton}
      style={{ position: 'relative', top: 3 }}
      onClick={() =>
        updateSearch({
          ordering: toggleArray(search.ordering ?? [], orderName),
        })
      }
    />
  );

  const updateSearchOrRemove = (field: keyof DeliveryRequestSearchQuery) => (v: any) =>
    updateSearch({
      page: 1,
      [field]: v ? v : undefined,
    });

  return (
    <>
      {renderDialog()}
      {renderSpinnerModal()}
      <Table columnFixedUntil={4} fixedWidths={[60, 60, 120, 80]}>
        <Table.Head>
          <Table.Row
            columns={[
              cellFactory.createCheckable(allCheckedAndNotEmpty, toggleCheckAll),
              cellFactory.create('NO.'),
              cellFactory.create(deliveryRequestStrings.d2dNo),
              cellFactory.createBlank(),
              cellFactory.create(deliveryRequestStrings.deliveryRequestFile),
              cellFactory.createSelectable(
                strings.salespersonJapan,
                [
                  { label: deliveryRequestStrings.myCases, value: true },
                  { label: 'ALL', value: false },
                ],
                (isMine) => {
                  updateSearch({
                    ...search,
                    page: 1,
                    salesperson_japan_id: isMine ? user.data?.id : undefined,
                  });
                },
              ),
              cellFactory.create(deliveryRequestStrings.consignee),
              cellFactory.createSelectable(
                deliveryRequestStrings.status,
                [
                  ...DeliveryRequestStatusName.options.map((option) => ({
                    label: option,
                    value: option,
                  })),
                  {
                    label: 'ALL',
                    value: '',
                  },
                ],
                updateSearchOrRemove('status'),
              ),
              cellFactory.createSelectable(
                customsRequestStrings.status,
                [
                  ...CustomsRequestStatusName.options.map((option) => ({
                    label: option,
                    value: option,
                  })),
                  {
                    label: '-',
                    value: '-',
                  },
                  {
                    label: 'ALL',
                    value: '',
                  },
                ],
                updateSearchOrRemove('customs_request_status'),
              ),
              cellFactory.create(
                <>
                  {deliveryRequestStrings.pickupDate} {renderSortButton(PICKUP_DATE_ORDER)}
                </>,
              ),
              cellFactory.create(deliveryRequestStrings.pickupAddress),
              cellFactory.create(
                <>
                  {deliveryRequestStrings.deliveryDate} {renderSortButton('-delivery_date')}
                </>,
              ),
              cellFactory.create(deliveryRequestStrings.receiver),
              cellFactory.create(deliveryRequestStrings.truckCompany),
              cellFactory.create(deliveryRequestStrings.truckTypeAndCount),
              cellFactory.create(
                <>
                  {deliveryRequestStrings.cargoQuantity}
                  <br />
                  PKG
                </>,
              ),
              cellFactory.create(
                <>
                  {deliveryRequestStrings.cargoWeight}
                  <br />
                  KGS
                </>,
              ),
              cellFactory.create(
                <>
                  {deliveryRequestStrings.cargoMeasurement}
                  <br />
                  CBM
                </>,
              ),
              cellFactory.createSelectable(
                deliveryRequestStrings.deliveryStatus,
                [
                  {
                    label: '-',
                    value: 'お届け中' satisfies DeliveryRequestDeliveryStatusName,
                  },
                  {
                    label: 'お届け完了' satisfies DeliveryRequestDeliveryStatusName,
                    value: 'お届け完了' satisfies DeliveryRequestDeliveryStatusName,
                  },
                  {
                    label: 'ALL',
                    value: undefined,
                  },
                ],
                updateSearchOrRemove('delivery_status'),
              ),
              cellFactory.create(deliveryRequestStrings.deliveryPrice),
              cellFactory.create(deliveryRequestStrings.deliveryRequestNo),
            ]}
          />
        </Table.Head>

        <Table.Body>
          {items.map((item, i) => {
            return (
              <Table.Row
                key={item.id}
                columns={[
                  cellFactory.createCheckable(checked(item.id), (checked) =>
                    setChecked(item.id, checked),
                  ),
                  cellFactory.create(<>{initialIndex + i}</>),
                  cellFactory.create(<>{item.booking_inquiry.d2d_no}</>),
                  cellFactory.create(
                    <a
                      className={classes.link}
                      target="_blank"
                      href={`${url('/admin/booking/BookingDetails')}?d2dNo=${
                        item.booking_inquiry.d2d_no
                      }&type=current&tabActiveId=preAlert`}
                    >
                      {strings.details}
                    </a>,
                  ),
                  cellFactory.create(
                    <a
                      className={clsx(classes.link, classes.whitespacePre)}
                      href={
                        url('/admin/booking/DeliveryRequestDetails') +
                        `?d2d_no=${item.booking_inquiry.d2d_no}&id=${item.id}`
                      }
                      target="_blank"
                    >
                      {DeliveryRequestHelper.getFileName(item)}
                    </a>,
                  ),
                  cellFactory.create(item.booking_inquiry.salesperson_japan?.last_name || '-'),
                  cellFactory.create(
                    <div
                      className={classes.preEllipsis}
                      style={{ maxWidth: 100 }}
                      title={item.booking_inquiry.consignee?.company_name}
                    >
                      {item.booking_inquiry.consignee?.company_name}
                    </div>,
                  ),
                  cellFactory.create(renderDeliveryRequestStatus(item)),
                  cellFactory.create(
                    item.customs_request?.status.length
                      ? renderCustomsRequestStatus(item.customs_request?.status.at(-1)!)
                      : '-',
                  ),
                  cellFactory.create(formatDate(item.pickup_date, 'short') || '-'),
                  cellFactory.create(
                    pipe(resolveDeliveryDestinationOptions(item.pickup_address), (d) => (
                      <div
                        className={classes.preEllipsis}
                        style={{ maxWidth: 51 }}
                        title={
                          d.delivery_destination_code === 'RANDOM'
                            ? d.repository
                            : `${d.d2d_system_name}(${d.repository})`
                        }
                      >
                        {d.delivery_destination_code === 'RANDOM'
                          ? d.repository
                          : d.d2d_system_name}
                      </div>
                    )),
                  ),
                  cellFactory.create(formatDate(item.delivery_date, 'short') || '-'),
                  cellFactory.create(
                    pipe(
                      item.delivery_instruction?.receiver?.address1 ??
                        (item.booking_inquiry.receiver?.address1 || '-'),
                      (finalAddress) => (
                        <div style={{ ...lineClamp(2), width: 70 }} title={finalAddress}>
                          {finalAddress}
                        </div>
                      ),
                    ),
                  ),
                  cellFactory.create(
                    <div className={classes.preEllipsis} style={{ maxWidth: 120 }}>
                      {item.partner?.name_en || '-'}
                      {DeliveryRequestHelper.getType(item) === 'yokomochi' && (
                        <>
                          <br />
                          <span className={clsx(classes.smallerText, classes.textGray500)}>
                            {deliveryRequestStrings.yokomochi}:{' '}
                            {item.yokomochi_partner?.name_en ?? '-'}
                          </span>
                        </>
                      )}
                    </div>,
                  ),
                  cellFactory.create(
                    <div className={classes.whitespacePre}>
                      {item.truck_type}
                      {' x '}
                      {item.truck_count}
                    </div>,
                  ),
                  cellFactory.create(
                    <div className={classes.whitespacePre}>{item.pre_alert.quantity}</div>,
                  ),
                  cellFactory.create(
                    <div className={classes.whitespacePre}>
                      {Number(item.pre_alert.gross_weight)}
                    </div>,
                  ),
                  cellFactory.create(
                    <div className={classes.whitespacePre}>
                      {Number(item.pre_alert.measurement)}
                    </div>,
                  ),
                  cellFactory.create(
                    <DeliveryRequestDeliveryStatusDisplayEdit deliveryRequest={item} />,
                  ),
                  cellFactory.create(
                    <DeliveryRequestDeliveryPriceDisplayEdit deliveryRequest={item} />,
                  ),
                  cellFactory.create(
                    <div
                      className={classes.preEllipsis}
                      style={{ width: 80 }}
                      title={item.delivery_request_no}
                    >
                      {item.delivery_request_no}
                    </div>,
                  ),
                ]}
              />
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
});

const DeliveryRequestsSearchForm = () => {
  const { search, updateSearch } = useSearch(DeliveryRequestSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const initialOutput = useMemo(
    () => ({
      ...search,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    }),
    [search],
  );

  const form = useForm(DeliveryRequestSearchQuery, {
    initialOutput,
  });

  const { fields } = form.root;

  useEffect(() => {
    form.root.setOutput(initialOutput);
  }, [search]);

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, [
          'd2d_no',
          'delivery_request_no',
          'consignee',
          'pickup_address',
          'receiver',
          'truck_type',
          'truck_count',
          'partner',
        ]),
        form,
      ),
    [fields],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <TextInput
        field={fields.pickup_date_after}
        inputProps={{ placeholder: getFieldLabel(fields.pickup_date_after) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.pickup_date_before}
        inputProps={{ placeholder: getFieldLabel(fields.pickup_date_before) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div>{/* gap */}</div>

      <TextInput
        field={fields.delivery_date_after}
        inputProps={{ placeholder: getFieldLabel(fields.delivery_date_after) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.delivery_date_before}
        inputProps={{ placeholder: getFieldLabel(fields.delivery_date_before) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div className={classes.buttonContainer}>
        <Button
          text={formStrings.filter}
          color="primary"
          type="submit"
          size="small"
          shape="round"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
          }}
        />
        <DebugForm form={form} />
      </div>
    </form>
  );
};

export const DeliveryRequests: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [batchSubmitAction, setBatchSubmitAction] =
    useState<Exclude<DeliveryRequestBatchSubmitAction, 'BATCH_SENT'>>();

  const [batchSentDialogOpen, setBatchSentDialogOpen] = useState(false);

  const [exportCSVOpen, setExportCSVOpen] = useState(false);

  const { search } = useSearch(DeliveryRequestSearchQuery);

  const deliveryRequestsQuery = dashboardDeliveryRequestApis.useGet('/dashboard/delivery-request', {
    queries: {
      ...search,
      ordering: search.ordering?.length ? search.ordering : [PICKUP_DATE_ORDER],
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    },
  });

  const deliveryRequestsTableChecklist = useChecklist(
    [],
    (deliveryRequestsQuery.data?.results ?? []).map((item) => item.id),
  );

  return (
    <Layout title={strings.title}>
      <div className={classes.container}>
        <SectionTitle
          actions={
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                shape="round"
                text={deliveryRequestStrings.mailSubmission}
                onClick={() => setBatchSubmitAction('BATCH_MAIL_SUBMIT')}
              />
              <Button
                color="primary"
                shape="round"
                text={deliveryRequestStrings.yokomochiCsvExport}
                onClick={() => setBatchSubmitAction('BATCH_CSV_SUBMIT')}
              />
              <Button
                color="primary"
                shape="round"
                text={deliveryRequestStrings.yokomochiSubmission}
                onClick={() => setBatchSubmitAction('BATCH_YOKOMOCHI_SUBMIT')}
              />
              <Button
                color="primary"
                shape="round"
                text={deliveryRequestStrings.deliverySentTitle}
                onClick={() => setBatchSentDialogOpen(true)}
              />
              <Button
                color="primary"
                shape="round"
                text={strings.exportCSV}
                onClick={() => setExportCSVOpen(true)}
              />
            </div>
          }
        >
          {strings.title}
        </SectionTitle>

        <DeliveryRequestsSearchForm />

        <div className={clsx(classes.flexInline, classes.justifyBetween)}>
          <DeliveryStatusFilter />
          <PageSizeForm />
        </div>

        <div style={{ height: 16 }} />

        <QueryContainer queries={[deliveryRequestsQuery]}>
          <DeliveryRequestTable
            initialIndex={1}
            items={deliveryRequestsQuery.data?.results ?? []}
            checklist={deliveryRequestsTableChecklist}
          />

          <div className={classes.paginationContainer}>
            <PaginationWithSearchQuery query={deliveryRequestsQuery} />
          </div>
        </QueryContainer>

        <DeliveryRequestBatchSubmitDialog
          open={!!batchSubmitAction}
          onClose={() => setBatchSubmitAction(undefined)}
          action={batchSubmitAction!}
          deliveryRequests={(deliveryRequestsQuery.data?.results ?? []).filter((d) =>
            deliveryRequestsTableChecklist.checked(d.id),
          )}
        />

        <DeliveryRequestBatchSentDialog
          open={batchSentDialogOpen}
          onClose={() => setBatchSentDialogOpen(false)}
          deliveryRequests={(deliveryRequestsQuery.data?.results ?? []).filter((d) =>
            deliveryRequestsTableChecklist.checked(d.id),
          )}
          type="batch"
        />

        <ExportCSVDialog2
          open={exportCSVOpen}
          onClose={() => setExportCSVOpen(false)}
          subject={deliveryRequestStrings.deliveryCase}
          baseUrl="/dashboard/delivery-request"
          selected={deliveryRequestsTableChecklist.checklist}
          query={search}
        />
      </div>
    </Layout>
  );
};
